// @givmo/app-admin — Steps Competitions LIST (EP-WEB10).
//
// The management landing for a company's walking competitions. Reads
// `GET /admin/company/steps-competitions` (DRAFTs included) and renders a row per competition
// (name · state badge · window · prize · entrants). "Create competition" opens the form; a row opens
// the detail/manage page.
//
// The prize column shows THIS competition's own immutable `prize_amount`, never a wallet figure — the
// wallet money context only arrives on the detail read, and conflating the two is the exact mistake the
// iOS held-prize fix (SC-IOS4) exists to prevent.
//
// DARK: the whole area is gated by `isCompanyStepsCompetitionsEnabled()`
// (VITE_COMPANY_STEPS_COMPETITIONS_ENABLED, default off). The `/competitions` route redirects when off;
// this page ALSO bounces (defense in depth) so it can never paint while dark.

import { useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { sdk, type AdminStepsCompetitionOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  PageHeader,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { Footprints, Plus } from 'lucide-react';
import { isCompanyStepsCompetitionsEnabled } from '../../lib/env';
import { useEmployerModulePaths } from '../../lib/module-paths';
import {
  competitionStatusView,
  errorMessage,
  extractCompetitions,
  formatDay,
  formatPrize,
} from './competitions-decode';

const PREFIX = 'competitions';
const PAGE_LIMIT = 25;
const LIST_KEY = ['steps_competitions_admin-list_admin_steps_competitions', 'page'] as const;

async function fetchPage(offset: number, signal: AbortSignal): Promise<AdminStepsCompetitionOut[]> {
  const { data } = await sdk.stepsCompetitionsAdminListAdminStepsCompetitions({
    query: { 'page[limit]': PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractCompetitions(data);
}

export function CompetitionsPage() {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();
  const enabled = isCompanyStepsCompetitionsEnabled();

  // Defense in depth: even though the /competitions route redirects when off, bounce here too so the
  // surface can never paint while dark (e.g. if the route gate ever regresses).
  useEffect(() => {
    if (!enabled) void navigate({ to: paths.dashboard });
  }, [enabled, navigate, paths.dashboard]);
  if (!enabled) return null;

  return <CompetitionsList />;
}

function CompetitionsList() {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();

  const list = useInfiniteQuery({
    queryKey: LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof LIST_KEY, number>) =>
      fetchPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const competitions = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  const columns: DataTableColumn<AdminStepsCompetitionOut>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Competition',
        width: '38%',
        cell: (c) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-w600 text-text-strong">{c.name}</span>
            {c.description !== null &&
              c.description !== undefined &&
              c.description.trim() !== '' && (
                <span className="truncate text-s12 text-text-tertiary">{c.description}</span>
              )}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'State',
        width: '150px',
        cell: (c) => {
          const status = competitionStatusView(c.status);
          return (
            <Badge tone={status.tone} dot testId={`${PREFIX}-status-${c.id}`}>
              {status.label}
            </Badge>
          );
        },
      },
      {
        id: 'window',
        header: 'Dates',
        width: '190px',
        cell: (c) => (
          <span className="text-s13 text-text-secondary tabular-nums">
            {formatDay(c.start_date)} – {formatDay(c.end_date)}
          </span>
        ),
      },
      {
        id: 'prize',
        header: 'Prize',
        width: '110px',
        align: 'right',
        cell: (c) => (
          <span
            className="tabular-nums text-text-strong"
            {...testIdProp(`${PREFIX}-prize-${c.id}`)}
          >
            {formatPrize(c)}
          </span>
        ),
      },
      {
        id: 'entrants',
        header: 'Entrants',
        width: '100px',
        align: 'right',
        cell: (c) => (
          <span className="tabular-nums text-text-secondary">{c.participant_count ?? 0}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-competitions')}
    >
      <PageHeader
        title="Competitions"
        subtitle="Run a steps competition for your employees. The prize is a company-funded donation to the winner’s charity."
        actions={
          <Button
            appearance="saas"
            tone="primary"
            leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
            onClick={() => void navigate({ to: paths.competitionNew })}
            testId={`${PREFIX}-create`}
          >
            Create competition
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={competitions}
        rowKey={(c) => c.id}
        rowTestId={(c) => `${PREFIX}-row-${c.id}`}
        onRowClick={(c) =>
          void navigate({ to: paths.competitionDetail, params: { competitionId: c.id } })
        }
        loading={list.isPending}
        loadingTestId={`${PREFIX}-loading`}
        error={list.isError ? errorMessage(list.error, 'Failed to load competitions.') : undefined}
        onRetry={() => void list.refetch()}
        errorTestId={`${PREFIX}-error`}
        retryTestId={`${PREFIX}-retry`}
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={Footprints} size={20} aria-hidden />}
            title="No competitions yet"
            description="Create a steps competition to get your team moving for a cause."
            action={
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                onClick={() => void navigate({ to: paths.competitionNew })}
                testId={`${PREFIX}-empty-create`}
              >
                Create competition
              </Button>
            }
          />
        }
        footer={
          list.hasNextPage === true ? (
            <>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                Showing {competitions.length} competitions
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${PREFIX}-load-more`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </>
          ) : undefined
        }
      />
    </div>
  );
}
